<template>
  <app-page class="ts-page" :class="{ 'ts-page--gestapelt': toolbarStacked }" fill lean>
    <!-- Aktionen rechts in der Titelzeile, rechtsbündig über den Bedienelementen der Werkzeugleiste und mittig zum Titel
         wie auf dem Dashboard (Kundenwunsch 12.10.2026); Handy: Knopfzeile im Kopf. Zuerst „Alle Gruppen auf-/zuklappen“:
         immer sichtbar, ohne Gruppierung gesperrt (Kundenwunsch 13.10.2026). Alle Aktionen mit Icon und Beschriftung
         (Kundenwunsch 13.10.2026), auf dem Handy als Icon-Knöpfe mit „Mehr“ -->
    <template #actions>
      <app-button
        v-if="actionMode === 'text'"
        :icon="expanded ? 'fold' : 'unfold'"
        :disabled="!grouped"
        @click="collapseAllClick"
      >{{ expanded ? 'Alle zuklappen' : 'Alle aufklappen' }}</app-button>
      <app-icon-button
        v-else
        :icon="expanded ? 'fold' : 'unfold'"
        :label="expanded ? 'Alle Gruppen zuklappen' : 'Alle Gruppen aufklappen'"
        :disabled="!grouped"
        @click="collapseAllClick"
      />
      <template v-if="actionMode === 'text'">
        <!-- Ansicht (Kundenwunsch 17.10.2026): kompakt (neu) oder klassisch wie früher mit allen Spalten und Filterzeile -->
        <app-segment
          class="ts-ansicht"
          label="Ansicht"
          :value="classic ? 'klassisch' : 'kompakt'"
          :items="viewItems"
          @input="setView($event === 'klassisch')"
        />
        <app-button icon="refresh" @click="refreshDataGrid">Aktualisieren</app-button>
        <app-button icon="printer" title="Zusammenfassung drucken" @click="printSummary">Drucken</app-button>
        <app-button icon="download" @click="exportToExcel">Excel-Export</app-button>
      </template>
      <template v-else>
        <app-icon-button icon="refresh" label="Aktualisieren" @click="refreshDataGrid" />
        <div class="ts-more">
          <v-menu ref="moreMenu" offset-y left nudge-bottom="4" min-width="240" content-class="app-menu">
            <template v-slot:activator="{ on, attrs }">
              <app-icon-button icon="more" label="Mehr" v-bind="attrs" v-on="on" />
            </template>
            <div class="app-menu__list" role="none">
              <button type="button" class="v-list-item app-menu__item" role="menuitem" @click="setView(!classic)">
                <span class="app-menu__text">{{ classic ? "Kompakte Ansicht" : "Klassische Ansicht" }}</span>
              </button>
              <button type="button" class="v-list-item app-menu__item" role="menuitem" @click="printSummary">
                <span class="app-menu__text">Zusammenfassung drucken</span>
              </button>
              <button type="button" class="v-list-item app-menu__item" role="menuitem" @click="exportToExcel">
                <span class="app-menu__text">Excel-Export</span>
              </button>
            </div>
          </v-menu>
        </div>
      </template>
    </template>

    <!-- Werkzeugleiste (Kundenwünsche 12.10.2026): links Zeitraum und Suche, rechts Filter, Sortierung und Gruppierung
         (Auf-/Zuklappen steht seit 13.10.2026 in der Titelzeile). Reicht eine Zeile nicht, stehen Zeitraum und Suche oben (die Suche füllt den
         Rest der Zeile) und die Bedienelemente rechtsbündig darunter (toolbarStacked; Tablet hoch über die Regeln von
         AppPage). Handy: Zeitraum, darunter die Suche in eigener Zeile, darunter die Bedienelemente linksbündig wie die
         Knopfzeile -->
    <template #toolbar>
      <period-field
        :period-type="periodType"
        :from-month="fromMonth"
        :month="month"
        :from-date="fromDate"
        :date="date"
        @apply="onPeriodApply"
      />
      <app-search
        v-model="searchText"
        class="ts-search"
        placeholder="In Verkäufen suchen"
        @input="onSearchInput"
      />
    </template>

    <template #toolbar-end>
      <!-- Menüs rechtsbündig zum Knopf, solange die Bedienelemente rechts stehen (Handy linksbündig); passt ein Menü dort
           nicht ins Fenster, öffnet es zur anderen Seite -->
      <!-- Filter und Sortierung der früheren Filterzeile (Kundenwunsch 12.10.2026); Fahrer und Zahlart stehen im Panel -->
      <!-- nur in der kompakten Ansicht; die klassische filtert wie früher in der Filterzeile unter dem Kopf -->
      <sale-filter-menu
        v-if="!classic"
        ref="filterMenu"
        :value="filters"
        :facets="facets"
        :result-count="summary.count"
        :description="filterTooltip"
        :align-right="menusRight"
        @open="onFilterMenuOpen"
        @close="onFilterMenuClose"
        @input="onFiltersInput"
      />
      <!-- Icon statt Präfix (Kundenwunsch 12.10.2026); „Sortierung: …“ und „Gruppierung: …“ bleiben zugänglicher Name
           und Tooltip. Mindestbreite für die üblichen Werte: kein Springen der Leiste beim Wechsel -->
      <sale-sort-menu
        :value="sortSpec"
        :min-width="fieldMinWidth"
        :align-right="menusRight"
        @input="onSortInput"
      />
      <!-- frei wählbare Gruppierung mit bis zu drei Ebenen, die bisherigen Gruppierungen als Vorlagen (Kundenwunsch
           12.10.2026) -->
      <sale-group-menu
        ref="groupMenu"
        :value="groupLevels"
        :fields="groupFields"
        :min-width="fieldMinWidth"
        :align-right="menusRight"
        @open="onGroupMenuOpen"
        @close="onGroupMenuClose"
        @input="onGroupLevels"
      />
    </template>

    <div class="ts-gridcol" :class="{ 'ts-gridcol--natural': handy }">
      <div ref="gridFill" class="app-grid-fill" :class="{ 'ts-gridfill--leer': filterEmpty }">
        <dx-data-grid
          :ref="gridRefName"
          :data-source="datasource"
          :columns="columns"
          :height="gridHeight"
          :show-borders="false"
          :show-column-lines="false"
          :show-row-lines="true"
          :column-auto-width="classic"
          :allow-column-reordering="!gridTouch"
          :allow-column-resizing="!gridTouch"
          :hover-state-enabled="!gridTouch"
          :focused-row-enabled="true"
          :element-attr="gridAttr"
          :scrolling="scrolling"
          @focused-row-changed="onFocusedRowChanged"
          @content-ready="onContentReady"
          @toolbar-preparing="onToolbarPreparing($event)"
        >
          <dx-group-panel :visible="false" />
          <dx-state-storing
            :enabled="false"
            type="custom"
            storage-key="ticketSaleStorage"
            :customLoad="customLoad"
            :customSave="customSave"
          />

          <DxColumnChooser :enabled="false" />
          <DxColumnFixing :enabled="false" />

          <!-- Gruppen beginnen zugeklappt; „Alle auf-/zuklappen“ über expandAll/collapseAll (collapseAllClick) -->
          <dx-grouping :auto-expand-all="false" expandMode="rowClick" />
          <!-- nur Anzeige: „Verkauft am“ zeigt die Sortierung ohne Index („2“) der ausgeblendeten Spalte Verkaufsdatum -->
          <dx-sorting :show-sort-indexes="false" />
          <dx-paging :page-size="defaultPageSize" />
          <dx-pager :visible="false" />
          <!-- Filterzeile nur in der klassischen Ansicht (Kundenwunsch 17.10.2026) -->
          <dx-filter-row :visible="classic" />
          <dx-export
            :enabled="false"
            :allow-export-selected-data="true"
            :customize-excel-cell="customizeExcelCell"
            file-name="Verkäufe"
            :texts="{
              exportAll: 'Alles herunterladen',
              exportSelectedRows: 'Auswahl herunterladen',
              exportTo: 'Excel Download',
            }"
          />

          <dx-selection mode="none" />

          <dx-summary>
            <!-- Gruppenzeile „Preis: 1,90 € (1 Verkauf, Summe: 1,90 €)“: gezählt werden Verkäufe wie in Fußleiste und Filter,
                 Beträge deutsch wie überall auf der Seite (bis 12.10.2026 „1 Tickets“ und Beträge im Format des
                 Browsers); gilt auch für die Gruppenzeilen in Excel -->
            <dx-group-item
              :align-by-column="false"
              column="sale_id"
              summary-type="count"
              :customize-text="groupCountText"
            />
            <dx-group-item
              :align-by-column="false"
              :show-in-group-footer="false"
              column="price"
              summary-type="sum"
              :customize-text="groupSumText"
            />

            <dx-group-item
              :align-by-column="true"
              :show-in-group-footer="true"
              column="price"
              summary-type="sum"
              :customize-text="groupSumText"
            />

            <dx-total-item
              column="price"
              summary-type="sum"
              :customize-text="totalSumText"
            />
          </dx-summary>

          <!-- Bildschirmansicht (nur Anzeige; Excel liest caption, Werte, format und alignment der Spalten) -->
          <template #zeitZelle="{ data }">
            <span>{{ saleTimeText(data.data) }}</span>
          </template>
          <template #ticketZelle="{ data }">
            <div class="ts-ticket">
              <span class="ts-ticket__name">
                <span class="ts-ellipsis">{{ placeText(data.data.ticket_name) }}</span>
                <app-tag v-if="data.data.is_cancellation == 1" kind="storno" />
              </span>
              <span class="ts-ticket__route">
                <span class="ts-ellipsis">{{ placeText(data.data.location_from_name) }}</span>
                <app-icon name="arrowR" :size="12" class="ts-ticket__arrow" />
                <span class="ts-ellipsis">{{ placeText(data.data.location_to_name) }}</span>
              </span>
            </div>
          </template>
          <!-- Fahrer mit vollem Namen wie Filter, Gruppen und Detail; ein Login ohne Benutzer (z. B. gelöscht) erscheint als
               Login statt leer (Excel und Suche nutzen weiter das Lookup der Spalte) -->
          <template #fahrerZelle="{ data }">
            <span>{{ driverName(data.data.username) }}</span>
          </template>
          <!-- Zahlart mit Symbol: Bar, Karte (auch externes Terminal) oder Gutschein (zahlart.js) -->
          <template #zahlartZelle="{ data }">
            <span class="ts-zahlart">
              <app-icon :name="zahlartVon(data.data).icon" :size="16" class="ts-zahlart__icon" />
              <span>{{ zahlartVon(data.data).text }}</span>
            </span>
          </template>
          <template #preisZelle="{ data }">
            <span class="ts-preis">{{ euro(data.data.price) }}</span>
          </template>
          <template #pfeilZelle>
            <app-icon name="chevR" :size="16" class="ts-pfeil" />
          </template>
          <template #fahrerKopf>
            <span>Fahrer</span>
          </template>
          <template #persKopf>
            <span>Pers.</span>
          </template>
        </dx-data-grid>
        <!-- Leerzustand nach Filter oder Suche (z. B. gewählte Werte, die es im neuen Zeitraum nicht gibt); Titel, Icon
             und Knopf nennen, was einschränkt: Filter, Suche oder beides -->
        <app-state-overlay
          v-if="filterEmpty"
          state="empty"
          :icon="emptyState.icon"
          :title="emptyState.title"
          :text="filterEmptyText"
        >
          <app-button @click="resetAllFilters">{{ emptyState.reset }}</app-button>
        </app-state-overlay>
      </div>
      <app-grid-footer ref="fuss" :page-sizes="pageSizes">
        <template #summary>
          <span>Summe <strong>{{ summaryTotal }}</strong></span>
          <span class="app-sep" aria-hidden="true">·</span>
          <!-- mit Filter oder Suche: „45 von 180 Verkäufen“ (ohne geladene Verkäufe nur „0 Verkäufe“) -->
          <span v-if="narrowed && summary.loaded > 0"><strong>{{ summaryCount }}</strong> von {{ summaryLoaded }} {{ summaryLoadedWord }}</span>
          <span v-else><strong>{{ summaryCount }}</strong> {{ summaryCountWord }}</span>
          <!-- beim Storno-Filter sagt die Anzahl schon alles. Handy: in eigener Zeile ohne Trenner (sonst blieb der Trenner
               beim Umbruch am Zeilenende hängen) -->
          <template v-if="!filters.storno">
            <span v-if="!handy" class="app-sep" aria-hidden="true">·</span>
            <span :class="{ 'ts-fuss-zeile': handy }">davon <strong>{{ summaryCancelled }}</strong> storniert</span>
          </template>
        </template>
      </app-grid-footer>

      <!-- Bestätigung im Design von „Mandant wechseln“ (Kundenwunsch 13.10.2026): grauer Rahmen, weiße innere Karte,
           weichgezeichneter Hintergrund; Bestätigen orange wie dort „Anmelden“ -->
      <app-dialog v-model="dialog" title="Verkauf stornieren" :max-width="440" persistent blur nested>
        Sind Sie sicher, dass Sie den Verkauf
        {{ cancelData ? cancelData.sale_id : "" }} stornieren möchten?
        <template #actions>
          <app-button @click="dialog = false">Abbrechen</app-button>
          <app-button kind="primary" @click="cancel">Stornieren</app-button>
        </template>
      </app-dialog>
      <!-- Druckblatt „Zusammenfassung“: nur während des Drucks, unsichtbar außerhalb des Fensters gerendert -->
      <div v-if="summarySheet" class="ts-print-source" aria-hidden="true">
        <sale-summary-sheet ref="summarySheet" v-bind="summarySheet" />
      </div>
      <!-- Toast unten rechts wie bei PayPal (Kundenwunsch 13.10.2026): Zusammenfassung mit Fortschritt, Fehler rot -->
      <app-toast
        v-model="snackbar"
        :text="snackbarText"
        :icon="snackbarError ? 'alert' : 'printer'"
        :kind="snackbarError ? 'error' : 'info'"
        :indeterminate="!snackbarError"
        closable
      />
    </div>

    <template #detail>
      <app-detail-pane
        :open="!!selectedSale"
        icon="receipt"
        title="Verkauf"
        @close="closeDetail"
        @layout-change="onDetailLayout"
      >
        <template #subtitle>
          <span v-if="selectedSale">#{{ selectedSale.sale_id }}</span>
          <app-tag v-if="selectedSale && selectedSale.is_cancellation == 1" kind="storno" />
        </template>
        <sale-detail
          v-if="selectedSale"
          :sale="selectedSale"
          :driver-name="driverName(selectedSale.username)"
        />
        <template v-if="canCancelSelected" #footer>
          <!-- weiß wie die übrigen Knöpfe, Icon „Ticket mit Kreuz“ (Kundenwunsch 13.10.2026) -->
          <app-button icon="ticketX" @click="cancelSaleButtonPressed(selectedSale)">Stornieren …</app-button>
        </template>
      </app-detail-pane>
    </template>
  </app-page>
</template>

<script>
import {
  DxDataGrid,
  DxPaging,
  DxFilterRow,
  DxPager,
  DxExport,
  DxGrouping,
  DxGroupPanel,
  DxGroupItem,
  DxSummary,
  DxSelection,
  DxTotalItem,
  DxStateStoring,
  DxColumnChooser,
  DxColumnFixing,
  DxSorting,
} from "devextreme-vue/data-grid";
// import DxButton from "devextreme-vue/button";
import deMessages from "devextreme/localization/messages/de.json";
import { locale, loadMessages } from "devextreme/localization";
import CustomStore from "devextreme/data/custom_store";
import query from "devextreme/data/query";
import axios from "axios";
import moment from "moment";
import PeriodField from "@/components/sales/PeriodField";
import SaleDetail from "@/components/sales/SaleDetail";
import SaleFilterMenu from "@/components/sales/SaleFilterMenu";
import SaleSortMenu from "@/components/sales/SaleSortMenu";
import SaleGroupMenu from "@/components/sales/SaleGroupMenu";
import SaleSummarySheet from "@/components/sales/SaleSummarySheet";
import { zahlartInfo } from "@/components/sales/zahlart";
import { loadLetterheadLogo, loadCustomer, letterheadOf, printSheetsHtml, simaPrintLogoUrl } from "@/utils/printSheet";
import {
  emptyFilters,
  activeCount,
  buildPredicate,
  buildFacets,
  describeFilters,
  NO_VALUE_TEXT,
  DEFAULT_SORT,
  normalizeSortSpec,
  sameSortSpec,
  sortDateOrder,
  findSortField,
  sortSpecFor,
  foldText,
  naturalKey,
} from "@/components/sales/saleFilters";
import {
  groupColumns,
  groupColumnName,
  GROUP_COLUMN_NAMES,
  availableGroupFields,
  groupOrder,
  normalizeLevels,
  sameLevels,
} from "@/components/sales/saleGrouping";
import { raiseMenu } from "@/components/ui/AppSelect.vue";
import { hasPermission } from "@/auth/auth";
import DataGridMixin from "@/mixins/DataGridMixin";
import { exportWithColumns } from "@/utils/gridExport";
import {
  formatEuro,
  formatNumber,
  formatDateTime,
  formatDateTimeShort,
  plural,
  LEER,
} from "@/utils/format";
const cjson = require("compressed-json");

const SUMMARY_TEXT = "Zusammenfassung wird vorbereitet …";
// Werkzeugleiste (Kundenwünsche 12.10.2026: Zeitraum und Suche links, Bedienelemente rechts): Mindestbreite der Suche
// in der gemeinsamen Zeile und Abstand der Leiste. Passen Zeitraum und Suche nicht neben die Bedienelemente, wird
// gestapelt.
const SEARCH_MIN = 240;
const TOOLBAR_GAP = 8;
// Mindestbreite der Felder „Sortierung“ und „Gruppierung“ mit Icon und Chevron: gemessen breitester Wert des
// Sortiermenüs „Preis · niedrigster zuerst“ (Maus 216, Touch 238) und längste Vorlage „Fahrer › Linie › Tarifzonen“
// (224, 246)
const SORT_MIN_WIDTH = { maus: 224, touch: 248 };

// Bildschirmansicht (Spezifikation 4.2.5): sichtbar sind nur diese Spalten in dieser Reihenfolge. Alle übrigen
// Spalten bleiben unverändert definiert (Excel-Export, Suche, Gruppierung) und sind ausgeblendet.
const SCREEN_COLUMNS = [
  "verkauft_am",
  "ticket_strecke",
  "line_id",
  "username",
  "payment_method",
  "ticket_count",
  "price",
  "pfeil",
];
// nur Darstellung (Maus-Breiten; die Touch-Breiten setzt applyScreenView zur Laufzeit). Als Funktion, damit jede
// Spaltendefinition eigene Objekte bekommt.
function screenOptions() {
  return {
    line_id: { width: 60, cssClass: "ts-linie" },
    username: { width: 150, headerCellTemplate: "fahrerKopf", cellTemplate: "fahrerZelle" },
    payment_method: { width: 120, cellTemplate: "zahlartZelle" },
    ticket_count: { width: 56, headerCellTemplate: "persKopf", cssClass: "app-zahl" },
    price: { width: 104, cellTemplate: "preisZelle", cssClass: "app-zahl" },
  };
}
// Excel: ohne diese Spalten ergibt der Export dieselben 36 Spalten wie bisher (die Hilfsspalten der Gruppierung
// erscheinen nur als Gruppenzeilen)
const EXPORT_EXCLUDE = [
  "sale_date_grouping",
  "line_id_grouping",
  "verkauft_am",
  "ticket_strecke",
  "pfeil",
].concat(GROUP_COLUMN_NAMES);

// Sortierwerte (Kundenwunsch 12.10.2026): je Rohwert nur einmal berechnet, denn DevExtreme ruft calculateSortValue bei
// jedem Vergleich auf. Werte, Gruppen, Suche und Export bleiben unverändert.
function cachedSortValue(field, toKey) {
  const cache = new Map();
  return function (rowData) {
    const raw = rowData[field];
    if (!cache.has(raw)) {
      cache.set(raw, toKey(raw));
    }
    return cache.get(raw);
  };
}
// Texte für „Sortierung: … A–Z“ ohne Groß-/Kleinschreibung und Akzente („Ötting“ bei O, nicht hinter Z)
function textSortValue(field) {
  return cachedSortValue(field, foldText);
}

function toScreenView(columns) {
  const options = screenOptions();
  return columns.map((column) => {
    const key = column.name || column.dataField;
    const index = SCREEN_COLUMNS.indexOf(key);
    const screen = Object.assign({}, column, options[key], {
      // › erst zur Laufzeit und nur bei Touch (applyScreenView)
      visible: index !== -1 && key !== "pfeil",
    });
    if (index !== -1) {
      screen.visibleIndex = index;
    }
    return screen;
  });
}

// Klassische Ansicht (Kundenwunsch 17.10.2026: „ansicht umschalten zwischen unserer neuen ansicht und der alten mit
// allen spalten und auch mit allen spaltenfiltern“): alle Spalten des Exports in Definitionsreihenfolge mit den
// schlichten Werten von früher (ohne die Darstellungen der Bildschirmansicht); Bildschirm- und Hilfsspalten verborgen
function toClassicView(columns) {
  let index = 0;
  return columns.map((column) => {
    const key = column.name || column.dataField;
    const visible = EXPORT_EXCLUDE.indexOf(key) === -1;
    // Mindestbreite aus der Überschrift samt Sortierpfeil: die automatische Breite rechnet den Pfeil nicht mit und
    // kürzte sonst „Verkaufsdatum“ zu „Verkaufsd…“
    const minWidth = Math.max(column.minWidth || 0, Math.round(String(column.caption || "").length * 7.5) + 52);
    const classic = Object.assign({}, column, { visible, minWidth });
    if (visible) {
      classic.visibleIndex = index++;
    }
    return classic;
  });
}

export default {
  name: "TicketSale",
  components: {
    DxDataGrid,
    DxPaging,
    DxFilterRow,
    DxPager,
    DxExport,
    DxGrouping,
    DxGroupPanel,
    DxGroupItem,
    DxSummary,
    DxSelection,
    DxTotalItem,
    DxStateStoring,
    DxColumnChooser,
    DxColumnFixing,
    DxSorting,
    PeriodField,
    SaleDetail,
    SaleFilterMenu,
    SaleSortMenu,
    SaleGroupMenu,
    SaleSummarySheet,
    // DxButton
  },
  mixins: [DataGridMixin],
  computed: {
    columns() {
      const base = [
        {
          dataField: "sale_date",
          caption: "Verkaufsdatum",
          // letzte Sortierstufe jeder Sortierung (applyToolbarView); sortIndex 1 nach „Verkauft am“ wie bei
          // „Neueste zuerst“, damit der Start keine zweite Sortierung auslöst
          sortOrder: "desc",
          sortIndex: 1,
          dataType: "date",
          calculateDisplayValue: function (rowData) {
            if (!rowData.sale_time) return null;
            return moment(rowData.sale_time).format("DD.MM.YYYY");
          },
          calculateSortValue: function (rowData) {
            return moment(rowData.sale_time).valueOf();
          },
        },
        {
          dataField: "sale_date",
          caption: "Verkaufsdatum",
          name: "sale_date_grouping",
          dataType: "string",
          visible: false,
          calculateDisplayValue: function (rowData) {
            if (!rowData.sale_time) return null;
            return moment(rowData.sale_time).format("DD.MM.YYYY");
          },
          calculateSortValue: function (rowData) {
            return moment(rowData.sale_time).valueOf();
          },
        },
        {
          dataField: "sale_time_time",
          caption: "Verkaufszeit",
          calculateDisplayValue: function (rowData) {
            if (!rowData.sale_time) return null;
            return moment(rowData.sale_time).format("HH:mm:ss");
          },
        },
        // {
        //   dataField: "sale_time_time",
        //   caption: "Verkaufszeit",
        //   alignment: "left",
        //   dataType: "time",
        //   sortOrder: "desc"
        // },
        {
          dataField: "sale_id",
          caption: "Verkaufs-ID",
          alignment: "left",
          summarytype: "count",
        },
        // {
        //   dataField: "local_id",
        //   caption: "Lokale Verkaufs-ID",
        //   alignment: "left"
        // },
        {
          dataField: "is_cancellation",
          caption: "Storno",
          alignment: "left",
          calculateCellValue: function (rowData) {
            return rowData.is_cancellation == 1 ? "X" : "";
          },
        },
        {
          dataField: "price",
          caption: "Preis",
          alignment: "right",
          dataType: "number",
          format: "######0.00 €",
        },
        {
          dataField: "ticket_count",
          caption: "Anzahl Personen",
          alignment: "left",
          dataType: "number",
        },
        {
          dataField: "logo_suffix",
          caption: "Mandant",
          alignment: "left",
        },
        {
          dataField: "line_id",
          caption: "Linie",
          alignment: "left",
          // Sortieren in natürlicher Folge („12“ vor „100“; Kundenwunsch 12.10.2026); Gruppen und Werte unverändert
          calculateSortValue: cachedSortValue("line_id", naturalKey),
        },
        {
          dataField: "line_id_grouping",
          caption: "Linie",
          alignment: "left",
          visible: false,
          calculateDisplayValue: function (rowData) {
            return rowData.line_id;
          },
        },
        {
          dataField: "is_outward_direction",
          caption: "Richtung",
          alignment: "left",
          format: function (value) {
            return value == 1 ? "H" : "R";
          },
        },
        {
          dataField: "trip_id",
          caption: "Fahrt",
          alignment: "left",
        },
        {
          dataField: "user_id",
          caption: "Fahrer-ID",
          alignment: "left",
        },
        {
          dataField: "username",
          caption: "Fahrername",
          alignment: "left",
          lookup: {
            dataSource: this.driverLookup,
            valueExpr: "username",
            displayExpr: "fullname",
          },
          // Sortieren nach dem angezeigten Namen statt nach dem Login (Kundenwunsch 12.10.2026); Gruppen unverändert.
          // Neu berechnet mit jeder Spaltendefinition, also auch nach dem Laden der Fahrer.
          calculateSortValue: cachedSortValue("username", (login) => foldText(this.driverName(login))),
        },
        {
          dataField: "printer_id",
          caption: "Drucker-ID",
          alignment: "left",
          // „Sortierung: Gerät“ in natürlicher Folge („P2“ vor „P10“; Kundenwunsch 12.10.2026); Werte unverändert
          calculateSortValue: cachedSortValue("printer_id", naturalKey),
        },
        {
          dataField: "location_from_id",
          caption: "Von-Ort-ID",
          alignment: "left",
        },

        {
          dataField: "location_from_name",
          caption: "Von-Ort",
          alignment: "left",
          calculateSortValue: textSortValue("location_from_name"),
        },
        {
          dataField: "location_to_id",
          caption: "Nach-Ort-ID",
          alignment: "left",
        },
        {
          dataField: "location_to_name",
          caption: "Nach-Ort",
          alignment: "left",
          calculateSortValue: textSortValue("location_to_name"),
        },
        {
          dataField: "ticket_type_id",
          caption: "Tickettyp-ID",
          alignment: "left",
        },
        {
          dataField: "ticket_name",
          caption: "Ticketbezeichnung",
          alignment: "left",
        },

        {
          dataField: "tariff_id",
          caption: "Tarif-ID",
          alignment: "left",
        },
        {
          dataField: "tariff_name",
          caption: "Tarifname",
          alignment: "left",
        },
        {
          dataField: "tariff_zone_id",
          caption: "Tarifzonen-ID",
          alignment: "left",
        },
        {
          dataField: "tariff_zone_combination",
          caption: "Tarif-Kombination",
          alignment: "left",
          calculateCellValue: function (row) {
            return [row.location_from_tariff_zone_1, row.location_to_tariff_zone_1]
              .filter((e) => e != null)
              .sort()
              .join("-");
          },
        },
        {
          dataField: "canceled_by_user_id",
          caption: "Storniert von ID",
          alignment: "left",
        },
        {
          dataField: "canceled_by_username",
          caption: "Storniert von Benutzer",
          alignment: "left",
        },
        {
          dataField: "payment_method",
          caption: "Zahlart",
          alignment: "left",
          // Bar, Kartenzahlung oder Gutschein (zahlart.js); auch Sortierwert der Spalte
          calculateCellValue: function (rowData) {
            return zahlartInfo(rowData).export;
          },
        },
        {
          dataField: "sumup_transaction_code",
          caption: "Sumup Transaktion",
          alignment: "left",
        },
        {
          dataField: "valid_to",
          caption: "Gültigkeit",
          alignment: "left",
        },

        {
          dataField: "comb_from",
          caption: "Von-Wabe",
          alignment: "left",
        },
        {
          dataField: "comb_to",
          caption: "Nach-Wabe",
          alignment: "left",
        },
        {
          dataField: "comb_alternative",
          caption: "Über-Wabe",
          alignment: "left",
        },
        {
          dataField: "passnr",
          caption: "Passnummer",
          alignment: "left",
        },
        {
          dataField: "passenger_name",
          caption: "Kunde",
          alignment: "left",
          calculateCellValue: function (rowData) {
            // If passenger_name exists, use it
            if (rowData.passenger_name) {
              return rowData.passenger_name;
            }

            // If passenger_name is null but custom_data exists, parse and format it
            if (!rowData.passenger_name && rowData.custom_data) {
              try {
                const customData = JSON.parse(rowData.custom_data);
                const parts = [];

                // Add person name
                const personName = [customData.firstName, customData.lastName]
                  .filter((part) => part && part.trim())
                  .join(" ");
                if (personName) parts.push(personName);

                // Add birthday
                if (customData.birthday) parts.push(`${customData.birthday}`);

                // Add adress
                if (customData.address) parts.push(`${customData.address}`);

                // city
                const city = [customData.postalCode, customData.city]
                  .filter((part) => part && part.trim())
                  .join(" ");
                if (city) parts.push(city);

                // Add guardian name if different from person
                const guardianName = [
                  customData.guardianFirstName,
                  customData.guardianLastName,
                ]
                  .filter((part) => part && part.trim())
                  .join(" ");
                if (guardianName && guardianName !== personName) {
                  parts.push(`Erz.-Ber.: ${guardianName}`);
                }

                // Add guardian birthday if different from main birthday
                if (
                  customData.guardianBirthday &&
                  customData.guardianBirthday !== customData.birthday
                ) {
                  parts.push(`${customData.guardianBirthday}`);
                }

                if(customData.remarks) parts.push(`Bemerkung: ${customData.remarks}`);

                return parts.join(", ");
              } catch (e) {
                // If JSON parsing fails, return null
                return null;
              }
            }

            return null;
          },
        },
        {
          dataField: "note",
          caption: "Appversion",
          alignment: "left",
        },
        {
          dataField: "rolle",
          caption: "Rolle",
          alignment: "left",
          calculateCellValue: function (rowData) {
            if (!rowData.custom_data) return null;
            try {
              const customData = JSON.parse(rowData.custom_data);
              if (!customData.rollNumbers || !customData.rollNumbers.length) return null;
              const rolls = [...new Set(customData.rollNumbers.map(r => r.substring(0, 6)))];
              return rolls.join(", ");
            } catch (e) {
              return null;
            }
          },
        },
        {
          dataField: "abschnitte",
          caption: "Abschnitte",
          alignment: "left",
          calculateCellValue: function (rowData) {
            if (!rowData.custom_data) return null;
            try {
              const customData = JSON.parse(rowData.custom_data);
              if (!customData.rollNumbers || !customData.rollNumbers.length) return null;
              return customData.rollNumbers.map(r => r.substring(6)).join(", ");
            } catch (e) {
              return null;
            }
          },
        },
        // {
        //   dataField: "speed",
        //   caption: "Geschwindigkeit",
        //   alignment: "left",
        // },
        // {
        //   dataField: "longitude",
        //   caption: "Längengrad",
        //   alignment: "left",
        // },
        // {
        //   dataField: "latitude",
        //   caption: "Breitengrad",
        //   alignment: "left",
        // },

        // {
        //   dataField: "creation_time",
        //   caption: "Erstellt",
        //   alignment: "left",
        //   dataType: "datetime",
        // },
        // {
        //   dataField: "modification_time",
        //   caption: "Geändert",
        //   alignment: "left",
        //   dataType: "datetime",
        // },
        // Bildschirmspalten (nicht im Excel-Export; die Suche läuft weiter über die bestehenden Spalten)
        {
          name: "verkauft_am",
          dataField: "sale_time",
          caption: "Verkauft am",
          width: 132,
          sortOrder: "desc",
          sortIndex: 0,
          allowExporting: false,
          allowSearch: false,
          calculateSortValue: function (rowData) {
            return moment(rowData.sale_time).valueOf();
          },
          cellTemplate: "zeitZelle",
        },
        {
          name: "ticket_strecke",
          dataField: "ticket_name",
          caption: "Ticket und Strecke",
          minWidth: 200,
          allowExporting: false,
          allowSearch: false,
          calculateSortValue: textSortValue("ticket_name"),
          cellTemplate: "ticketZelle",
        },
        {
          name: "pfeil",
          caption: "",
          width: 32,
          allowSorting: false,
          allowExporting: false,
          cssClass: "ts-pfeilspalte",
          cellTemplate: "pfeilZelle",
        },
        // Hilfsspalten der Gruppierung (Kundenwunsch 12.10.2026; saleGrouping.js): je Feld eine, immer verborgen, nicht
        // im Export, nicht in der Suche; Fahrer mit vollem Namen (bei gleichem Namen mit Login), „Fahrt“ je nach Ebenen
      ].concat(
        groupColumns({
          driverName: (login) => this.driverLabel(login),
          levels: () => this.groupLevels,
        })
      );
      return this.classic ? toClassicView(base) : toScreenView(base);
    },
    viewItems() {
      return [
        { value: "kompakt", text: "Kompakt" },
        { value: "klassisch", text: "Klassisch" },
      ];
    },
    // Fahrer der Spalte „Fahrername“ nur mit Login: Zellen ohne Wert (etwa die Gruppenfüße, seit die Spalte beim
    // Gruppieren stehen bleibt) zeigten sonst den Namen eines Benutzers ohne Login
    driverLookup() {
      const store = Array.isArray(this.driverDataSource.store) ? this.driverDataSource.store : [];
      return Object.assign({}, this.driverDataSource, { store: store.filter((d) => d && d.username) });
    },
    monthFormatted() {
      return moment(this.month).format("MM.YYYY");
    },
    fromMonthFormatted() {
      return moment(this.fromMonth).format("MM.YYYY");
    },
    dateFormatted() {
      return moment(this.date).format("DD.MM.YYYY");
    },
    fromDateFormatted() {
      return moment(this.fromDate).format("DD.MM.YYYY");
    },
    // ein Schlüssel für den Zeitraum: „Übernehmen“ setzt alle Werte zugleich, geladen wird genau einmal
    periodKey() {
      return [this.daily, this.fromMonth, this.month, this.fromDate, this.date].join("|");
    },
    // Jahr in „Verkauft am“ nur, wenn der geladene Zeitraum einen Jahreswechsel enthält
    crossesYear() {
      const from = String(this.daily ? this.fromDate : this.fromMonth).substring(0, 4);
      const to = String(this.daily ? this.date : this.month).substring(0, 4);
      return from !== to;
    },
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    // Handy: natürliche Höhe (Seite scrollt), sonst füllt das Grid den Bereich
    gridHeight() {
      return this.handy ? undefined : "100%";
    },
    // Aktionen der Titelzeile: beschriftet (Icon und Text, Kundenwunsch 13.10.2026), auf dem Handy Icon-Knöpfe mit „Mehr“
    actionMode() {
      return this.handy ? "menu" : "text";
    },
    // gleiche Regel wie AppDetailPane
    detailMode() {
      if (this.handy) {
        return "full";
      }
      const width = this.$vuetify && this.$vuetify.breakpoint ? this.$vuetify.breakpoint.width : 1280;
      return width >= 1264 ? "side" : "overlay";
    },
    // Bedienelemente der Werkzeugleiste rechts (alle Größen außer Handy): ihre Menüs öffnen rechtsbündig. Auf dem Handy
    // stehen sie linksbündig unter der Suche.
    menusRight() {
      return !this.handy;
    },
    // Tablet hoch: „Pers.“ ausblenden (der Wert steht im Detail)
    hidePersons() {
      return this.detailMode === "overlay" && this.gridWidth > 0 && this.gridWidth < 760;
    },
    // ---------------------------------------------------------------- Filter und Sortierung (Kundenwunsch 12.10.2026)
    filtersActive() {
      return activeCount(this.filters) > 0;
    },
    searchActive() {
      return String(this.searchText || "").trim() !== "";
    },
    // Filter oder Suche schränken die geladenen Verkäufe ein
    narrowed() {
      return this.filtersActive || this.searchActive;
    },
    // gesetzte Filter vollständig (Druck) und gekürzt (Tooltip des Filter-Knopfs, Leerzustand)
    filterDescription() {
      return describeFilters(this.filters, this.filterLabel);
    },
    filterTooltip() {
      const parts = describeFilters(this.filters, this.filterLabel, 3);
      return parts.length ? parts.join(" · ") : null;
    },
    filterEmpty() {
      return this.narrowed && this.summary.count === 0 && this.summary.loaded > 0;
    },
    filterEmptyText() {
      const parts = describeFilters(this.filters, this.filterLabel, 3);
      if (this.searchActive) {
        parts.push("Suche: „" + String(this.searchText).trim() + "“");
      }
      return parts.join(" · ");
    },
    // Leerzustand nennt, was einschränkt: nur die Suche, nur Filter oder beides (Titel, Icon, Knopf)
    emptyState() {
      if (this.searchActive && !this.filtersActive) {
        return { icon: "search", title: "Keine Verkäufe für diese Suche", reset: "Suche zurücksetzen" };
      }
      if (this.searchActive) {
        return { icon: "funnel", title: "Keine Verkäufe für Suche und Filter", reset: "Suche und Filter zurücksetzen" };
      }
      return { icon: "funnel", title: "Keine Verkäufe für diese Filter", reset: "Filter zurücksetzen" };
    },
    // volle Namen, die mehrere Benutzer tragen: Filterliste, Drucktext und Gruppen hängen dort den Login an
    ambiguousNames() {
      const seen = {};
      const twice = {};
      (this.driver || []).forEach((d) => {
        if (d && d.username && d.fullname) {
          if (seen[d.fullname]) {
            twice[d.fullname] = true;
          }
          seen[d.fullname] = true;
        }
      });
      return twice;
    },
    // Mindestbreite der Felder „Sortierung“ und „Gruppierung“ (beide gleich, SORT_MIN_WIDTH): die Leiste springt beim
    // Wechsel der üblichen Werte nicht, längere (freie Ebenen) verbreitern das Feld. Handy ohne: die Bedienelemente
    // brechen dort ohnehin um.
    fieldMinWidth() {
      if (this.handy) {
        return null;
      }
      return this.gridTouch ? SORT_MIN_WIDTH.touch : SORT_MIN_WIDTH.maus;
    },
    grouped() {
      return this.groupLevels.length > 0;
    },
    canCancelSelected() {
      return (
        !!this.selectedSale &&
        this.selectedSale.is_cancellation == 0 &&
        hasPermission("VIEW_ADMINISTRATION_TICKETSALE_CANCEL")
      );
    },
    summaryTotal() {
      return this.summary.total === null ? LEER : formatEuro(this.summary.total);
    },
    summaryCount() {
      return formatNumber(this.summary.count);
    },
    summaryCountWord() {
      return plural(this.summary.count, "Verkauf", "Verkäufe");
    },
    summaryLoaded() {
      return formatNumber(this.summary.loaded);
    },
    summaryLoadedWord() {
      return plural(this.summary.loaded, "Verkauf", "Verkäufen");
    },
    summaryCancelled() {
      return formatNumber(this.summary.cancelled);
    },
  },
  created() {
    loadMessages(deMessages);
    locale("de"); // immer Deutsch wie src/plugins/devextreme.js (vorher Browsersprache)
    // nicht reaktiv: geladene Zeilen (Fußleiste „davon … storniert“, Werte im Filter-Panel), Zeilen der zuletzt
    // berechneten Filterwerte und Gruppierungsfelder, Export läuft, Detail schließt, Bildschirmansicht nach dem
    // Dichtewechsel, Größenbeobachter (Tabelle, Werkzeugleiste)
    this._rows = [];
    this._facetRows = null;
    this._groupFieldRows = null;
    this._exporting = false;
    this._closing = false;
    this._closingTimer = null;
    this._screenTimer = null;
    this.resizeObserver = null;
    this.toolbarObserver = null;
  },
  watch: {
    periodKey: function () {
      this.closeDetail();
      this.refreshDataGrid();
    },
    // neue Spaltendefinition (nach dem Laden der Fahrer): DevExtreme legt die Spalten neu an, deshalb Bildschirmansicht
    // sowie Gruppierung und Sortierung aus der Werkzeugleiste erneut setzen (der Filter hängt an der Datenquelle und
    // bleibt)
    columns: function () {
      this.$nextTick(() => {
        this.applyScreenView();
        this.reapplyToolbarState();
      });
    },
    // Dichtewechsel: DevExtreme baut die Spalten neu auf (allowColumnResizing u. a.) und gruppiert dabei zugeklappt
    // neu. Erst danach die Bildschirmansicht setzen (im selben Durchlauf brach das Ausblenden von › bei Gruppierung das
    // Zeichnen der Gruppenzeilen ab) und „Alle aufgeklappt“ wiederherstellen.
    gridTouch: function () {
      clearTimeout(this._screenTimer);
      this._screenTimer = setTimeout(() => {
        this.applyScreenView();
        this.updateDimensions();
        const grid = this.getGrid();
        if (grid && this.grouped && this.expanded) {
          grid.expandAll();
        }
      }, 0);
    },
    hidePersons: function () {
      this.applyScreenView();
    },
    // Gruppierung an/aus: Bildschirmansicht (breitere Preisspalte) und Werkzeugleiste neu bemessen (Auf-/Zuklappen
    // erscheint bzw. verschwindet; in der gestapelten Form ändert das keine beobachtete Größe)
    grouped: function () {
      this.$nextTick(() => {
        this.applyScreenView();
        this.updateToolbarLayout();
      });
    },
    // Werkzeugleiste bricht um oder wird wieder einzeilig: offene Panels folgen ihrem Knopf (Lage und Höhe)
    toolbarStacked: function () {
      this.$nextTick(() => {
        ["filterMenu", "groupMenu"].forEach((name) => {
          const menu = this.$refs[name];
          if (menu && typeof menu.relayout === "function") {
            menu.relayout();
          }
        });
      });
    },
    handy: function () {
      this.$nextTick(this.updateDimensions);
    },
    actionMode: function () {
      this.$nextTick(this.raiseMoreMenu);
    },
  },
  data() {
    return {
      storageKey: "ticketSaleStorage",
      snackbar: false,
      snackbarText: SUMMARY_TEXT,
      snackbarError: false,
      daily: false,
      periodType: 'monthly',
      customer: this.$store.state.customerId,
      user: this.$store.state.cognitoUsername,
      month: new Date().toISOString().substr(0, 7),
      fromMonth: new Date().toISOString().substr(0, 7),
      date: moment().endOf("month").format("YYYY-MM-DD"),
      fromDate: moment().startOf("month").format("YYYY-MM-DD"),
      gridRefName: "dataGrid",
      gridAttr: { class: "grid-2z app-grid--ohne-summenzeile" },
      search: "",
      searchText: "",
      dialog: false,
      expanded: false,
      // Ansicht: kompakt (Standard) oder klassisch; wird nicht gespeichert (Kundenwunsch 17.10.2026)
      classic: false,
      cancelData: null,
      selectedSale: null,
      // Daten des Druckblatts „Zusammenfassung“ (nur während des Drucks)
      summarySheet: null,
      // Filter-Panel (saleFilters.js) und Sortierung als [[spalte, richtung], …]; nicht gespeichert
      filters: emptyFilters(),
      facets: [],
      filterMenuOpen: false,
      sortSpec: DEFAULT_SORT.slice(),
      // Gruppierung als Ebenen (saleGrouping.js), z. B. ['fahrer', 'tag'], und die Felder mit mindestens zwei Werten
      // für das Menü (beim Öffnen berechnet); nicht gespeichert
      groupLevels: [],
      groupFields: [],
      groupMenuOpen: false,
      // Werkzeugleiste gestapelt: Suche über die volle Breite, Bedienelemente rechtsbündig darunter
      toolbarStacked: false,
      summary: { total: null, count: null, cancelled: null, loaded: 0 },
      gridWidth: 0,
      driver: [],
      driverDataSource: {
        store: [],
      },
      datasource: {
        store: new CustomStore({
          key: "upload_id",
          load: () => {
            if (!this.daily) {
              var dateStart = moment(`${this.fromMonth}-01`);
              var dateEnd = moment(`${this.month}-01`);
              var timeValues = [];
              while (
                dateEnd > dateStart ||
                dateStart.format("M") === dateEnd.format("M")
              ) {
                timeValues.push({
                  month: dateStart.format("MM"),
                  year: dateStart.format("YYYY"),
                });
                dateStart.add(1, "month");
              }
              return new Promise((resolve) => {
                axios
                  .all(
                    timeValues.map((month) =>
                      axios.get(
                        `${this.$store.state.baseUrl}ticketsale?sale_month=${month.month}&sale_year=${month.year}&option=compress`
                      )
                    )
                  )
                  .then((responseArr) => {
                    let response = [];
                    for (var i = 0; i < responseArr.length; i++) {
                      response = response.concat(cjson.decompress(responseArr[i].data));
                    }
                    resolve(response);
                  });
              }).then((r) => {
                this._rows = Array.isArray(r) ? r : r.data;
                return r;
              });
            } else {
              return axios.get(
                `${this.$store.state.baseUrl}ticketsale?from_date=${moment(
                  this.fromDate
                ).format("YYYY-MM-DD")}&to_date=${moment(this.date).format("YYYY-MM-DD")}`
              ).then((r) => {
                this._rows = Array.isArray(r) ? r : r.data;
                return r;
              });
            }
          },
        }),
        reshapeOnPush: true,
      },
    };
  },
  mounted() {
    this.loadDriver();
    this.applyScreenView();
    this.raiseMoreMenu();
    this.observeGridWidth();
    this.observeToolbar();
  },
  beforeDestroy() {
    clearTimeout(this._closingTimer);
    clearTimeout(this._screenTimer);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.toolbarObserver) {
      this.toolbarObserver.disconnect();
      this.toolbarObserver = null;
    }
  },
  methods: {
    async loadDriver() {
      this.driver = (
        await axios.get(
          `${this.$store.state.baseUrl}user?customer_id=${this.$store.state.customerId}`
        )
      ).data;
      this.driverDataSource = {
        store: this.driver,
        sort: "fullname",
      };
    },
    onSearchInput() {
      // Apply search to DataGrid
      if (this.$refs[this.gridRefName] && this.$refs[this.gridRefName].instance) {
        this.$refs[this.gridRefName].instance.searchByText(this.searchText || '');
      }
    },
    hasPermission(p) {
      return hasPermission(p);
    },
    getGrid() {
      const ref = this.$refs[this.gridRefName];
      return ref && ref.instance ? ref.instance : null;
    },
    // Bildschirmansicht zur Laufzeit: Touch-Breiten, › nur bei Touch, „Pers.“ im Tablet hoch ausgeblendet,
    // breitere Preisspalte bei Gruppierung
    applyScreenView() {
      const grid = this.getGrid();
      if (!grid || this._exporting || this.classic) {
        return;
      }
      const touch = this.gridTouch;
      const wanted = {
        // Touch 124 statt 120: sonst kürzt DevExtreme „Verkauft am“ neben dem Sortierpfeil
        verkauft_am: { width: touch ? 124 : 132 },
        // Tablet hoch: schmalere Mindestbreite, damit die Zeile ohne waagerechtes Scrollen passt
        ticket_strecke: { minWidth: this.hidePersons ? 160 : 200 },
        username: { width: touch ? 120 : 150 },
        payment_method: { width: touch ? 112 : 120 },
        // gruppiert: Platz für die Gruppensumme im Gruppenfuß („Summe: 1234.56 €“)
        price: { width: this.grouped ? 152 : touch ? 88 : 104 },
        ticket_count: { visible: !this.hidePersons },
        pfeil: { visible: touch },
      };
      const changes = [];
      Object.keys(wanted).forEach((name) => {
        Object.keys(wanted[name]).forEach((option) => {
          if (grid.columnOption(name, option) !== wanted[name][option]) {
            changes.push([name, option, wanted[name][option]]);
          }
        });
      });
      // Sichtbarkeit in einem eigenen Schritt vor den Breiten: in einem Schritt zusammen zeichnete DevExtreme bei
      // Gruppierung die Gruppenzeilen mit den Summenzellen der alten Spaltenzahl und brach ab (› beim Wechsel Touch →
      // Maus)
      const visibility = changes.filter((change) => change[1] === "visible");
      const sizes = changes.filter((change) => change[1] !== "visible");
      [visibility, sizes].forEach((batch) => {
        if (!batch.length) {
          return;
        }
        grid.beginUpdate();
        try {
          batch.forEach((change) => grid.columnOption(change[0], change[1], change[2]));
        } finally {
          grid.endUpdate();
        }
      });
    },
    // Gruppierung und Sortierung der Werkzeugleiste nach einer neuen Spaltendefinition wieder anwenden
    reapplyToolbarState() {
      this.applyToolbarView();
    },
    updateDimensions() {
      const grid = this.getGrid();
      if (grid) {
        grid.updateDimensions();
      }
    },
    observeGridWidth() {
      const element = this.$refs.gridFill;
      if (!element || typeof window.ResizeObserver !== "function") {
        return;
      }
      this.resizeObserver = new window.ResizeObserver((entries) => {
        const width = Math.round(entries[0].contentRect.width);
        if (width !== this.gridWidth) {
          this.gridWidth = width;
        }
      });
      this.resizeObserver.observe(element);
    },
    // Werkzeugleiste beobachten: ihre Breite (Fenster, Navigation, Dichte), die des Zeitraums („Oktober 2026“,
    // „Sep.–Okt. 2026“) und die der Bedienelemente rechts (Werte von Sortierung und Gruppierung). Beobachtet werden die
    // Bedienelemente selbst: gestapelt ist ihr Behälter immer zeilenbreit. Auf-/Zuklappen kommt und geht mit der
    // Gruppierung (Watcher grouped).
    observeToolbar() {
      const toolbar = this.$el.querySelector(".app-page__toolbar");
      const end = this.$el.querySelector(".app-page__toolbar-end");
      const period = this.$el.querySelector(".app-page__toolbar > .period-field");
      if (!toolbar || !end || typeof window.ResizeObserver !== "function") {
        return;
      }
      this.toolbarObserver = new window.ResizeObserver(() => this.updateToolbarLayout());
      this.toolbarObserver.observe(toolbar);
      Array.prototype.forEach.call(end.children, (item) => this.toolbarObserver.observe(item));
      if (period) {
        this.toolbarObserver.observe(period);
      }
    },
    // Eine Zeile, solange Zeitraum und Suche (SEARCH_MIN) neben die Bedienelemente passen, sonst gestapelt. Gemessen
    // wird der Inhalt der Bedienelemente (Summe ihrer Breiten samt Abständen), nicht ihr Behälter: Der ist gestapelt
    // zeilenbreit, die Leiste wurde sonst nie wieder einzeilig (Prüfung 12.10.2026). Beide Formen ergeben dieselbe
    // Inhaltsbreite, die Messung schaukelt sich nicht auf. Tablet hoch und Handy stapeln ohnehin (Regeln von AppPage in
    // bedienelemente.css).
    updateToolbarLayout() {
      const page = this.$el;
      const toolbar = page.querySelector(".app-page__toolbar");
      const end = page.querySelector(".app-page__toolbar-end");
      const period = page.querySelector(".app-page__toolbar > .period-field");
      let stacked = false;
      if (
        toolbar &&
        end &&
        !page.classList.contains("app-page--narrow") &&
        !page.classList.contains("app-page--phone")
      ) {
        const style = window.getComputedStyle(toolbar);
        const inner = toolbar.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
        const start = (period ? period.getBoundingClientRect().width + TOOLBAR_GAP : 0) + SEARCH_MIN;
        const items = Array.prototype.filter.call(end.children, (item) => item.getBoundingClientRect().width > 0);
        const content =
          items.reduce((sum, item) => sum + item.getBoundingClientRect().width, 0) +
          TOOLBAR_GAP * Math.max(items.length - 1, 0);
        stacked = inner + 0.5 < start + TOOLBAR_GAP + content;
      }
      if (stacked !== this.toolbarStacked) {
        this.toolbarStacked = stacked;
      }
    },
    raiseMoreMenu() {
      raiseMenu(this.$refs.moreMenu);
    },
    onContentReady(e) {
      const grid = e.component;
      if (this.$refs.fuss) {
        this.$refs.fuss.update(grid);
      }
      this.updateSummary(grid);
      this.syncSortFromGrid(grid);
      // neu geladene Zeilen bei offenem Filter- bzw. Gruppierungs-Panel: Werte der Listen bzw. Felder nachziehen
      if (this.filterMenuOpen && this._facetRows !== this._rows) {
        this.refreshFacets();
      }
      if (this.groupMenuOpen && this._groupFieldRows !== this._rows) {
        this.refreshGroupFields();
      }
    },
    // Fußleiste: Summe und Anzahl aus dem Grid, Stornos aus den geladenen Zeilen mit demselben Filter
    updateSummary(grid) {
      const dataSource = grid.getDataSource();
      if (dataSource && dataSource.isLoading()) {
        return;
      }
      const rows = this._rows || [];
      let cancelled = 0;
      if (rows.length) {
        const filter = grid.getCombinedFilter();
        const visibleRows = filter ? query(rows).filter(filter).toArray() : rows;
        cancelled = visibleRows.filter((row) => row.is_cancellation == 1).length;
      }
      this.summary = {
        total: Number(grid.getTotalSummaryValue("price")) || 0,
        count: Number(grid.totalCount()) > 0 ? Number(grid.totalCount()) : 0,
        cancelled,
        loaded: rows.length,
      };
    },
    onFocusedRowChanged(e) {
      if (this._closing || !e.row || e.row.rowType !== "data") {
        return;
      }
      // flache, eingefrorene Kopie: Vue beobachtet die Grid-Zeile nicht
      this.selectedSale = Object.freeze(Object.assign({}, e.row.data));
    },
    closeDetail() {
      const wasOpen = !!this.selectedSale;
      this.selectedSale = null;
      this.resetFocusedRow();
      // Verschwindet der Detailbereich mit dem Fokus, stellt DevExtreme den Fokus der zuletzt gewählten Zeile
      // kurz danach wieder her (focusedRowChanged). Das darf das Detail nicht erneut öffnen.
      if (wasOpen) {
        this._closing = true;
        clearTimeout(this._closingTimer);
        this._closingTimer = setTimeout(() => {
          this._closing = false;
          this.resetFocusedRow();
        }, 300);
      }
    },
    resetFocusedRow() {
      const grid = this.getGrid();
      if (grid && grid.option("focusedRowIndex") !== -1) {
        grid.option("focusedRowIndex", -1);
      }
    },
    onDetailLayout() {
      this.updateDimensions();
    },
    driverName(username) {
      const driver = (this.driver || []).find((d) => d.username === username);
      return driver && driver.fullname ? driver.fullname : username || null;
    },
    // Fahrer in Filterliste, Drucktext und Gruppen: voller Name, tragen ihn mehrere Benutzer, mit Login („Anna Berger
    // (aberger2)“); ein Login ohne Benutzer bleibt der Login
    driverLabel(username) {
      const name = this.driverName(username);
      if (name && name !== username && this.ambiguousNames[name]) {
        return name + " (" + username + ")";
      }
      return name;
    },
    saleTimeText(row) {
      return this.crossesYear ? formatDateTime(row.sale_time) : formatDateTimeShort(row.sale_time);
    },
    placeText(value) {
      return value === null || value === undefined || value === "" ? LEER : value;
    },
    euro(value) {
      return formatEuro(value);
    },
    zahlartVon(row) {
      return zahlartInfo(row);
    },
    onPeriodApply(period) {
      this.periodType = period.periodType;
      this.daily = period.periodType === "daily";
      if (this.daily) {
        this.fromDate = period.from;
        this.date = period.to;
      } else {
        this.fromMonth = period.from;
        this.month = period.to;
      }
    },
    // ---------------------------------------------------------------- Filter (Kundenwunsch 12.10.2026)
    // Anzeigetext eines Listenwerts im Filter-Panel: Fahrer mit vollem Namen (driverLabel), leere Werte „ohne Angabe“
    filterLabel(listKey, key) {
      if (key === "") {
        return NO_VALUE_TEXT;
      }
      return listKey === "drivers" ? this.driverLabel(key) || key : key;
    },
    refreshFacets() {
      this._facetRows = this._rows;
      // eingefroren: Vue beobachtet die Listen nicht
      this.facets = Object.freeze(buildFacets(this._rows || [], this.filters, this.filterLabel));
    },
    onFilterMenuOpen() {
      this.filterMenuOpen = true;
      this.refreshFacets();
    },
    onFilterMenuClose() {
      this.filterMenuOpen = false;
    },
    // Ansicht umschalten, ohne neu zu laden. Kein Filter bleibt unsichtbar wirksam: in die klassische Ansicht ohne die
    // Filter des Panels (es ist dort ausgeblendet), zurück ohne die Filterzeile. Die neue Spaltendefinition setzt
    // Gruppierung und Sortierung über den Watcher von columns wieder.
    setView(classic) {
      if (classic === this.classic) {
        return;
      }
      const grid = this.getGrid();
      if (classic && this.filtersActive) {
        this.onFiltersInput(emptyFilters());
      }
      if (!classic && grid) {
        grid.clearFilter("row");
      }
      this.classic = classic;
      this.$nextTick(this.updateToolbarLayout);
    },
    onFiltersInput(filters) {
      this.filters = filters || emptyFilters();
      this.applyToolbarFilter();
    },
    // Filter als Prüffunktion auf der Datenquelle: wirkt auch auf Felder ohne sichtbare Spalte, übersteht den
    // Spalten-Neuaufbau und die Exportansicht; Fußleiste, Excel und getCombinedFilter berücksichtigen ihn. Nur im
    // Browser, ohne Anfrage an die Schnittstelle.
    applyToolbarFilter() {
      const grid = this.getGrid();
      if (!grid) {
        return;
      }
      const match = buildPredicate(this.filters);
      // gewählter Verkauf fällt heraus: Detail schließen
      if (match && this.selectedSale && !match(this.selectedSale)) {
        this.closeDetail();
      }
      // Form [fn, "=", true] wie die Spaltenfilter von DevExtreme; jedes Mal eine neue Funktion, sonst übergeht
      // grid.filter() die Änderung
      grid.filter(match ? [match, "=", true] : null);
    },
    // Leerzustand: Filter des Panels und Suche zurücksetzen. Der Knopf verschwindet mit dem Leerzustand: Fokus auf den
    // Filter-Knopf, war nur die Suche gesetzt, ins Suchfeld (sonst fiel er auf die Seite)
    resetAllFilters() {
      const onlySearch = this.searchActive && !this.filtersActive;
      this.filters = emptyFilters();
      this.applyToolbarFilter();
      if (this.searchActive) {
        this.searchText = "";
        this.onSearchInput();
      }
      this.$nextTick(() => {
        const target = this.$el.querySelector(onlySearch ? ".ts-search input" : ".sale-filter .app-btn");
        if (target) {
          target.focus();
        }
      });
    },
    // Filtertext der Druck-Zusammenfassung aus dem Zustand von Filter-Panel und Suche
    printFilterText() {
      const parts = this.filterDescription.slice();
      if (this.searchActive) {
        parts.push("Suche: „" + String(this.searchText).trim() + "“");
      }
      return parts.join("; ");
    },

    // ---------------------------------------------------------------- Sortierung, Gruppierung (Kundenwünsche 12.10.2026)
    onSortInput(spec) {
      this.sortSpec = normalizeSortSpec(spec);
      if (!this.sortSpec.length) {
        this.sortSpec = DEFAULT_SORT.slice();
      }
      this.applyToolbarView();
    },
    // Ebenen aus dem Menü „Gruppierung“; wie bei den bisherigen Gruppierungen beginnen die neuen Gruppen zugeklappt
    // (applyToolbarView)
    onGroupLevels(levels) {
      const next = normalizeLevels(levels);
      if (sameLevels(next, this.groupLevels)) {
        return;
      }
      this.groupLevels = next;
      this.applyToolbarView();
    },
    // Felder mit mindestens zwei Werten in den geladenen Zeilen (nur sie bietet das Menü an)
    refreshGroupFields() {
      this._groupFieldRows = this._rows;
      this.groupFields = Object.freeze(availableGroupFields(this._rows || []));
    },
    onGroupMenuOpen() {
      this.groupMenuOpen = true;
      this.refreshGroupFields();
    },
    onGroupMenuClose() {
      this.groupMenuOpen = false;
    },
    // nicht gruppierte Spalten mit Sortierung in ihrer Reihenfolge: [[name bzw. dataField, richtung], …]
    readGridSort(grid) {
      const entries = [];
      const count = grid.columnCount();
      for (let i = 0; i < count; i++) {
        const column = grid.columnOption(i);
        if (
          column.groupIndex === undefined &&
          column.sortIndex !== undefined &&
          (column.sortOrder === "asc" || column.sortOrder === "desc")
        ) {
          entries[column.sortIndex] = [column.name || column.dataField, column.sortOrder];
        }
      }
      return entries.filter(Boolean);
    },
    // gruppierte Spalten in Ebenenfolge: [{ name, order }, …]
    readGridGrouping(grid) {
      const groups = [];
      const count = grid.columnCount();
      for (let i = 0; i < count; i++) {
        const column = grid.columnOption(i);
        if (column.groupIndex !== undefined) {
          groups[column.groupIndex] = { name: column.name || column.dataField, order: column.sortOrder };
        }
      }
      return groups.filter(Boolean);
    },
    // Gruppierung und Sortierung der Werkzeugleiste ins Grid, in einem Schritt (eine Neusortierung). Gruppiert wird
    // über die Hilfsspalten der Ebenen; sortiert nach den gewählten Spalten und zuletzt nach dem Verkaufsdatum (gleiche
    // Richtung wie „Verkauft am“, sonst neueste zuerst), innerhalb der Gruppen. Die Gruppen jeder Ebene folgen
    // groupOrder (Datum und Monat wie die Verkaufszeit, sonst wie die Sortierung derselben Spalte, sonst aufsteigend).
    // Nach jeder Änderung von Sortierung oder Gruppierung und nach einem Spalten-Neuaufbau (reapplyToolbarState).
    applyToolbarView() {
      const grid = this.getGrid();
      if (!grid) {
        return;
      }
      if (!this.sortSpec.length) {
        this.sortSpec = DEFAULT_SORT.slice();
      }
      const spec = this.sortSpec;
      const target = spec.concat([["sale_date", sortDateOrder(spec)]]);
      const groups = this.groupLevels.map((key) => ({ name: groupColumnName(key), order: groupOrder(key, spec) }));
      const current = this.readGridGrouping(grid);
      const sameColumns =
        current.length === groups.length && groups.every((group, i) => current[i].name === group.name);
      const sameOrders = sameColumns && groups.every((group, i) => current[i].order === group.order);
      // schon so (etwa beim Start): nichts tun, jede Änderung sortiert alle geladenen Zeilen neu
      if (sameOrders && sameSortSpec(this.readGridSort(grid), target)) {
        return;
      }
      grid.beginUpdate();
      try {
        const count = grid.columnCount();
        // Gruppierung: bisherige aufheben, dann die Hilfsspalten der Ebenen in Folge, zugeklappt (autoExpandGroup: auch
        // wenn eine Spalte vorher mit „Alle aufklappen“ aufgeklappt war)
        if (!sameColumns) {
          for (let i = 0; i < count; i++) {
            if (grid.columnOption(i, "groupIndex") !== undefined) {
              grid.columnOption(i, "groupIndex", undefined);
            }
          }
          groups.forEach((group, i) => grid.columnOption(group.name, { groupIndex: i, autoExpandGroup: false }));
          this.expanded = false;
        }
        // Sortierung: alle nicht gruppierten Spalten zurücksetzen, dann die gewählten Spalten in Folge
        for (let i = 0; i < count; i++) {
          if (grid.columnOption(i, "groupIndex") === undefined && grid.columnOption(i, "sortOrder") !== undefined) {
            grid.columnOption(i, "sortOrder", undefined);
          }
        }
        target.forEach((entry, index) => {
          grid.columnOption(entry[0], { sortOrder: entry[1], sortIndex: index });
        });
        // Richtung der Gruppen je Ebene
        groups.forEach((group) => {
          if (grid.columnOption(group.name, "sortOrder") !== group.order) {
            grid.columnOption(group.name, "sortOrder", group.order);
          }
        });
      } finally {
        grid.endUpdate();
      }
    },
    // Sortierung des Grids lesen (nach Kopfklick): entspricht sie nicht der gewählten Sortierung, übernimmt das Feld
    // „Sortierung“ sie (Feld und Richtung oder Spalte wie „Zahlart · Z–A“). Ein Kopfklick sortiert nur die geklickte
    // Spalte; gehört sie zu einem Feld, gilt dessen ganze Sortierung („Linie“ → Linie und Fahrt), und applyToolbarView
    // ergänzt wie bei der Wahl im Menü das Verkaufsdatum (seit der Prüfung vom 12.10.2026 dieselbe Reihenfolge wie der
    // gleichnamige Menüeintrag). Gruppiert ordnet applyToolbarView die Gruppen einer Ebene derselben Spalte gleich mit.
    // Ohne Sortierung (Strg+Klick) bleibt es bei „Unsortiert“.
    syncSortFromGrid(grid) {
      const actual = normalizeSortSpec(this.readGridSort(grid));
      if (sameSortSpec(actual, normalizeSortSpec(this.sortSpec))) {
        return;
      }
      const found = findSortField(actual);
      this.sortSpec = found ? sortSpecFor(found.field.key, found.dir) : actual;
      if (this.sortSpec.length) {
        this.$nextTick(this.applyToolbarView);
      }
    },

    cancelSaleButtonPressed(sale) {
      this.dialog = true;
      this.cancelData = sale;
    },
    async cancel() {
      this.dialog = false;
      try {
        const data = Object.assign({}, this.cancelData);
        data.upload_time = null;
        data.upload_id = null;
        data.is_cancellation = 1;
        data.price = -Math.abs(data.price);
        data.sale_time = moment().utc().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z";
        data.canceled_by_user_id = this.$store.state.profile.user_id;
        data.canceled_by_username = this.$store.state.profile.username;
        data.sale_id = data.sale_id.split("S").join("");
        data.note = `webapp-v${process.env.VUE_APP_VERSION}`;
        await axios.post(
          `${this.$store.state.baseUrl}ticketsale?customer_id=${this.$store.state.customerId}`,
          data
        );
      } catch (e) {
        this.snackbarText = "Stornieren fehlgeschlagen. Bitte erneut versuchen.";
        this.snackbarError = true;
        this.snackbar = true;
        return;
      }
      this.closeDetail();
      this.refreshDataGrid();
    },
    refreshDataGrid() {
      this.$refs[this.gridRefName].instance.refresh();
    },
    customLoad() {
      var state = localStorage.getItem(this.storageKey);
      if (state) {
        state = JSON.parse(state);
        // Siehe Saldo.vue: doppelte Spaltennamen aus einem alten Zustand
        // lassen DevExtreme mit E1059 abbrechen, das Grid bleibt dann leer.
        // Diese Seite hat mit sale_date/sale_time_time ebenfalls mehrfach
        // belegte dataFields und damit dasselbe Risiko.
        var columns = state.columns || [];
        var names = columns.map(function (column) {
          return column.name || column.dataField;
        });
        if (new Set(names).size !== names.length) {
          localStorage.removeItem(this.storageKey);
          return null;
        }
        for (var i = 0; i < columns.length; i++) {
          columns[i].filterValue = null;
        }
      }
      return state;
    },
    customSave(state) {
      localStorage.setItem(this.storageKey, JSON.stringify(state));
    },
    // Zusammenfassung drucken (Kundenwunsch 13.10.2026): Inhalt wie die frühere Word-Vorlage (Zeitraum, Filter, Summe,
    // Datum), Kopf, Fuß und Erzeugung im Browser wie die Fahrerabrechnung. Summe der Preise der geladenen Zeilen mit
    // demselben Filter wie Grid und Fußleiste (Filter-Panel und Suche), Druck über ein unsichtbares iframe.
    async printSummary() {
      const grid = this.getGrid();
      if (!grid) {
        return;
      }
      this.snackbarText = SUMMARY_TEXT;
      this.snackbarError = false;
      this.snackbar = true;
      try {
        const rows = this._rows || [];
        const filter = grid.getCombinedFilter();
        const visibleRows = filter && rows.length ? query(rows).filter(filter).toArray() : rows;
        const [logoUrl, customer] = await Promise.all([
          loadLetterheadLogo(this.$store.state.baseUrl),
          loadCustomer(this.$store),
        ]);
        this.summarySheet = {
          sum: Math.round(visibleRows.reduce((total, row) => total + (Number(row.price) || 0), 0) * 100) / 100,
          letterhead: letterheadOf(customer, this.$store.state.customerName),
          logoUrl,
          simaLogoUrl: simaPrintLogoUrl(),
          periodText: this.printPeriodText(),
          filterText: this.printFilterText(),
          createdAt: moment().format("DD.MM.YYYY HH:mm"),
        };
        await this.$nextTick();
        const sheet = this.$refs.summarySheet;
        if (sheet && sheet.$el) {
          await printSheetsHtml(sheet.$el.outerHTML, "Zusammenfassung Verkäufe");
        }
      } finally {
        this.snackbar = false;
        this.summarySheet = null;
      }
    },
    // „Oktober 2026“, „September – Oktober 2026“, „Dezember 2025 – Januar 2026“ bzw. „01.10.2026 – 08.10.2026“
    printPeriodText() {
      if (this.daily) {
        const from = moment(this.fromDate).format("DD.MM.YYYY");
        const to = moment(this.date).format("DD.MM.YYYY");
        return from === to ? from : `${from} – ${to}`;
      }
      const from = moment(this.fromMonth);
      const to = moment(this.month);
      if (from.isSame(to, "month")) {
        return to.format("MMMM YYYY");
      }
      return from.isSame(to, "year")
        ? `${from.format("MMMM")} – ${to.format("MMMM YYYY")}`
        : `${from.format("MMMM YYYY")} – ${to.format("MMMM YYYY")}`;
    },
    // Excel in der Exportansicht (alle bisherigen Spalten), danach wieder die Bildschirmansicht. Lädt das Grid
    // gerade, startet kein Export (exportWithColumns liefert false) und die Ansicht bleibt unverändert.
    // Klassische Ansicht (seit 17.10.2026): genau die Tabelle, wie sie dasteht – alle Spalten wie früher, Filter aus
    // Filterzeile und Suche; ohne Gruppierung zellgleich zum früheren Export.
    exportToExcel() {
      const grid = this.getGrid();
      if (!grid) {
        return;
      }
      if (this.classic) {
        grid.exportToExcel(false);
        return;
      }
      const started = exportWithColumns(grid, {
        exclude: EXPORT_EXCLUDE,
        onRestore: () => {
          this._exporting = false;
          this.$nextTick(this.applyScreenView);
        },
      });
      // erst nach dem Umschalten sperren: applyScreenView läuft nur aus Watchern, also frühestens im nächsten Tick
      if (started) {
        this._exporting = true;
      }
    },
    onToolbarPreparing() {
      // Buttons are now in the filter row, no toolbar buttons needed
    },
    // Alle Gruppen auf- bzw. zuklappen über die Schnittstelle des Grids (seit 12.10.2026): Die Zeilen kommen aus dem
    // Zwischenspeicher, Filter und Sortierung bleiben. Bis dahin schaltete der Knopf grouping.autoExpandAll um;
    // DevExtreme setzte dabei die Datenquelle neu auf, lud den Zeitraum neu und verwarf den Filter des Panels.
    collapseAllClick() {
      const grid = this.getGrid();
      if (!grid) {
        return;
      }
      this.expanded = !this.expanded;
      if (this.expanded) {
        grid.expandAll();
      } else {
        grid.collapseAll();
      }
    },
    // Gruppensummen (Bildschirm und Excel): Anzahl als „1 Verkauf“/„43 Verkäufe“, Beträge deutsch („1.234,56 €“)
    groupCountText(info) {
      return formatNumber(info.value) + " " + plural(info.value, "Verkauf", "Verkäufe");
    },
    groupSumText(info) {
      return "Summe: " + formatEuro(info.value);
    },
    // Summenzeile nur in Excel (auf dem Bildschirm ausgeblendet): Schreibweise wie der frühere Export („Gesamtsumme:
    // 1097,70 €“, ohne Tausenderpunkt), damit Abrechnungen, in die der Export unverändert übernommen wird, weiter passen
    // (Kundenrückmeldung 16.10.2026)
    totalSumText(info) {
      return "Gesamtsumme: " + (Number(info.value) || 0).toFixed(2).replace(".", ",") + " €";
    },
    customizeExcelCell(options) {
      var gridCell = options.gridCell;
      if (!gridCell) {
        return;
      }
      if (gridCell.rowType === "data") {
        if (gridCell.column.dataField === "sale_id") {
          options.format = "sollte als text formatiert werdent";
        }
        if (gridCell.column.dataField === "price") {
          options.numberFormat = "########0.00 €";
        }
      }
    },
  },
};
</script>

<style scoped>
/* Druckblatt „Zusammenfassung“: nur als HTML-Quelle für das Druck-iframe, außerhalb des Fensters */
.ts-print-source {
  position: fixed;
  top: 0;
  left: -10000px;
  width: 760px;
  pointer-events: none;
}
.ts-gridcol {
  flex: 999 1 560px;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
/* Handy: natürliche Höhe statt fester Fläche */
.ts-gridcol--natural .app-grid-fill {
  flex: none;
}
/* Handy: „davon … storniert“ in eigener Zeile der Fußleiste (ohne Trenner) */
.ts-fuss-zeile {
  flex-basis: 100%;
}
/* Handy: Platz für den Leerzustand nach Filter oder Suche (der leere Tabellenkörper ist sonst nur 100 hoch) */
.ts-gridcol--natural .ts-gridfill--leer {
  min-height: 240px;
}
.ts-more {
  display: inline-flex;
}
/* Zeitraum und Suche links (Kundenwünsche 12.10.2026): die Suche in der gemeinsamen Zeile mit den Bedienelementen 240
   bis 320 breit (Touch 360), sie wächst in den freien Platz; die feste Breite von AppSearch (Inline-Stil) gilt hier
   nicht */
.ts-search {
  flex: 1 1 240px !important;
  width: auto !important;
  max-width: 320px;
}
html.dichte-touch .ts-search {
  max-width: 360px;
}
/* gestapelt (toolbarStacked) und Tablet hoch: Zeitraum und Suche oben, die Suche füllt den Rest der Zeile; die
   Bedienelemente in eigener Zeile darunter (Tablet hoch über die Regeln von AppPage) */
.ts-page.ts-page--gestapelt .ts-search,
.ts-page.app-page--narrow .ts-search {
  max-width: none;
}
.ts-page.ts-page--gestapelt ::v-deep .app-page__toolbar-end {
  flex: 1 1 100%;
  margin-left: 0;
}
/* Bedienelemente rechtsbündig, auch in eigener Zeile (gestapelt, Tablet hoch); in einer Zeile sind sie ohnehin so
   breit wie ihr Inhalt. Handy linksbündig wie die Knopfzeile darüber. */
.ts-page:not(.app-page--phone) ::v-deep .app-page__toolbar-end {
  justify-content: flex-end;
}
/* Handy: Suche in eigener Zeile unter dem Zeitraum, darunter die Bedienelemente */
.ts-page.app-page--phone .ts-search {
  flex-basis: 100% !important;
  max-width: none;
}
.ts-gridcol ::v-deep .ts-ticket {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.ts-gridcol ::v-deep .ts-ticket__name {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-weight: 500;
}
.ts-gridcol ::v-deep .ts-ticket__name .app-tag {
  flex: none;
}
.ts-gridcol ::v-deep .ts-ticket__route {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
}
.ts-gridcol ::v-deep .ts-ticket__arrow {
  flex: none;
}
.ts-gridcol ::v-deep .ts-ellipsis {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ts-gridcol ::v-deep .ts-preis {
  font-weight: 500;
  color: var(--ink);
}
.ts-gridcol ::v-deep .ts-zahlart {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.ts-gridcol ::v-deep .ts-zahlart__icon {
  flex: none;
  color: var(--ink-3);
}
.ts-gridcol ::v-deep .ts-pfeil {
  display: block;
  color: var(--ink-3);
}
.ts-gridcol ::v-deep .dx-datagrid .dx-datagrid-rowsview .dx-data-row > td.ts-linie {
  font-weight: 500;
}
/* Kopf, Gruppenzeilen und Gruppenfuß (0 12, außen 20) sowie der mittige Aufklapp-Chevron kommen aus devextreme.css */
/* Endspalte › (Touch): 32 breit, Polster 0 12 0 4 */
.ts-gridcol ::v-deep .dx-datagrid .dx-datagrid-rowsview .dx-data-row > td.ts-pfeilspalte:not([class*="dx-command"]),
.ts-gridcol ::v-deep .dx-datagrid .dx-datagrid-headers .dx-header-row > td.ts-pfeilspalte:not([class*="dx-command"]) {
  padding: 0 12px 0 4px;
}
/* Gruppensummen in Zahlenspalten rechtsbündig */
.ts-gridcol ::v-deep .dx-datagrid td.app-zahl .dx-datagrid-summary-item {
  text-align: right !important;
}
</style>
